import { createContext, useContext } from 'react';

import type { Theme } from './ThemeProvider';

type ThemeCSSVariable = `--tfd-${string}-${string}`;

export interface ThemeContextValue {
  /* Get the current theme name for the given element, or the document root if not provided */
  getTheme: (element?: HTMLElement | null) => Theme | undefined;
  /* Get the current theme value for the theme variable and element, or the document root if not provided */
  getCSSVariable: (
    cssVariable: ThemeCSSVariable,
    element?: HTMLElement | null
  ) => string | undefined;
  /* Set the theme name for the given element, or the document root if not provided */
  setTheme: (theme: Theme | undefined, element?: HTMLElement | null) => void;
  /* Set a theme value for the given variable and element, or the document root if not provided.
   * If the theme value is empty or undefined, the value will be removed from the element's style properties.
   */
  setCSSVariable: (
    cssVariable: ThemeCSSVariable,
    cssValue: string | undefined,
    element?: HTMLElement | null
  ) => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(
  undefined
);

/** Return the stripe context value. */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme cannot be used outside of a ThemeProvider');
  }

  return context;
}
